import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import AgeGateway from '@age-gateway/screens/AgeGateway';
import NoAccess from '@age-gateway/screens/NoAccess';
import { fetchWithTimeout } from 'global-utils/api';
import { API_BASE_URL, AGEGATE_CONFIG_URL } from 'config/api';
import { getProjectInfo, getScriptInfo } from 'global-utils/get-attributes';
import { getPreferredLang } from 'global-utils/get-language';
import useTranslations from '@age-gateway/hooks/useTranslations';
import { availableLangs } from '@age-gateway/config/languages';
import { getOptimizedImageUrl } from '@age-gateway/utils/image-utils';
import { getTheme, getLogoURL } from './utils/get-theme';
import ThemeProvider from './providers/ThemeProvider';
import { sendErrorLog } from 'global-utils/audit';
import { userGaveConsent } from 'global-utils/cookie-utils';
import { sendCloseEvent } from 'global-utils/globals';

const App = () => {
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  // const loadingScreen = document.getElementById('cbg-loading-screen');

  useEffect(() => {
    const { projectId } = getProjectInfo();
    const { env } = getScriptInfo();

    // live is the default env value for the age gateway configuration
    const currentEnv = env || 'live';
    // eslint-disable-next-line consistent-return
    const fetchConfig = async (): Promise<void> => {
      const URL = `${API_BASE_URL}/${projectId}/${AGEGATE_CONFIG_URL}/${currentEnv}`;

      try {
        const result = (await fetchWithTimeout(URL)) || null; // Replace with your actual endpoint

        if (result.status === 404) {
          console.error(
            'cbg_agegate:: failed to fetch configuration as the prjId/market config does not exist'
          );
          sendErrorLog({
            errorCode: '404',
            origin: 'agegate-config'
          });
          // loadingScreen?.remove();
        }

        setConfig(result);
        setLoadingConfig(false);
        // loadingScreen?.remove();
      } catch (err) {
        console.error('age gateway config aborted and applied defaults');
        setConfig(null);
        setLoadingConfig(false);
        if (err.name === 'AbortError') {
          sendErrorLog({
            errorCode: 'cancelled',
            origin: 'agegate-config'
          });
        } else {
          sendErrorLog({
            errorCode: err.message,
            origin: 'agegate-config'
          });
        }
        // loadingScreen?.remove();
      }
    };

    fetchConfig();
  }, []);

  const { brandName } = config || {};
  const noConfig = !config;
  const [noAccess, setNoAccess] = useState(false);
  const theme = config?.ageGateway?.style || null;
  const customTheme = getTheme(theme);
  const logoUrl = theme ? getLogoURL(theme?.logoUrl) : null;
  const optimizedLogoUrl = getOptimizedImageUrl(logoUrl, 145);

  const backgroundImageUrl: string | null = getOptimizedImageUrl(
    config?.ageGateway?.style?.bgImageUrl,
    620
  );

  const [isOpen, setIsOpen] = useState(true);

  const currentLang = getPreferredLang(availableLangs);
  const { translations } = useTranslations(currentLang);

  const onCloseAgeGate = () => {
    setIsOpen(false);
    const isCookiesClosed = userGaveConsent();
    if (isCookiesClosed) {
      sendCloseEvent();
    }
  };

  return isOpen
    ? ReactDOM.createPortal(
        loadingConfig ? null : (
          <ThemeProvider theme={customTheme}>
            {noAccess ? (
              <NoAccess
                brandName={brandName}
                logoUrl={optimizedLogoUrl}
                translations={translations}
              />
            ) : (
              <AgeGateway
                brandName={brandName}
                logoUrl={optimizedLogoUrl}
                setNoAccess={setNoAccess}
                onCloseAgeGate={onCloseAgeGate}
                backgroundImageUrl={backgroundImageUrl}
                noConfig={noConfig}
                translations={translations}
              />
            )}
          </ThemeProvider>
        ),
        document.body
      )
    : null;
};

const MemoizedApp = React.memo(App);

export default MemoizedApp;
